import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, Offcanvas, Spinner } from "react-bootstrap";
import { apiCall } from "../../utils/axiosService";
import { API_URL } from "../../utils/apiRoutes";
import { commonService } from "../../utils/commonService";
import { useSelector } from "react-redux";
import { apiResponse } from "../../utils/apiResponse";
import { HubConnectionBuilder } from "@microsoft/signalr";
import moment from "moment";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import userPlaceHolder from "../../Assets/Images/blank-user.svg";
import RMCopilotChart from "../CommonComponent/RMCopilotChart";

function PropertyChat({ show, handleClose }) {
    const userDetail = commonService.getLoginUserData();

    const propertyDetailFromRTK = useSelector(
        (store) => store.headerProperty.propertyDetail
    );


    const [RMAsDefaultUserData, setRMAsDefaultUserData] = useState({
        displayname: "",
        userid: 0,
        email: "",
        profilepic: ""
    });
    const [userList, setUserList] = useState([]);
    const [messages, setMessages] = useState([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const [inputMessage, setInputMessage] = useState("");
    const [taggedUsers, setTaggedUsers] = useState([]);
    const [rmCopilotThinking, setRmCopilotThinking] = useState(false);

    // Mention popup state
    const [showMentionPopup, setShowMentionPopup] = useState(false);
    const [filteredMentionUsers, setFilteredMentionUsers] = useState([]);
    const [atIndex, setAtIndex] = useState(-1);

    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    const hubConnectionRef = useRef(null);

    // Fetch user list for the property for tagging
    const getUserRolePropertyList = async () => {
        if (!propertyDetailFromRTK?.propertyid) return;
        let userRolePropertyResponse = await apiCall(
            {
                method: "POST",
                url:
                    API_URL.BASE_API_URL.UserManagement +
                    API_URL.USER_ROLE_PROPERTY.GET_LIST_BY_PROPERTY_AND_MODULE,
                body: {
                    propertyid: parseInt(propertyDetailFromRTK.propertyid),
                    modulename: "revenue",
                },
            },
            false
        );

        let response = apiResponse(false, userRolePropertyResponse);

        if (response?.isValidate) {
            let list = response?.data?.data.map((x) => ({
                displayname: x.displayname || x.email,
                userid: x.userid,
                email: x.email,
                profilepic: x.profilepic,
            }));

            let RMUser = response?.data?.data.filter((u) => u.displayname.toLowerCase() === "rm copilot");
            if (RMUser && RMUser.length > 0) {
                setRMAsDefaultUserData(RMUser[0]);
            }

            setUserList(list || []);
        } else {
            setUserList([]);
        }
    };

    // Fetch chat history
    const fetchChatHistory = async (page = 1, isPrepend = false) => {
        if (!propertyDetailFromRTK?.propertyid) return;

        if (isPrepend) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }

        try {
            let response = await apiCall(
                {
                    method: "POST",
                    url: API_URL.BASE_API_URL.Notification + API_URL.PROPERTY_CHAT.GET_LIST,
                    body: {
                        propertyid: parseInt(propertyDetailFromRTK.propertyid),
                        pageindex: page,
                        pagesize: 20,
                    },
                },
                false
            );

            if (response?.status_code === 1 && response?.data) {
                const fetchedMessages = response.data.messages || [];
                const moreAvailable = response.data.hasmore || false;

                setHasMore(moreAvailable);
                setPageIndex(page);

                if (isPrepend) {
                    const chatContainer = chatContainerRef.current;
                    const oldScrollHeight = chatContainer ? chatContainer.scrollHeight : 0;

                    setMessages((prev) => [...fetchedMessages, ...prev]);

                    setTimeout(() => {
                        if (chatContainer) {
                            const newScrollHeight = chatContainer.scrollHeight;
                            chatContainer.scrollTop = newScrollHeight - oldScrollHeight;
                        }
                    }, 50);
                } else {
                    setMessages(fetchedMessages);
                    setTimeout(() => {
                        if (chatContainerRef.current) {
                            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                        }
                    }, 100);
                }
            }
        } catch (error) {
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Initialize SignalR and load data on show / property change
    useEffect(() => {
        if (show && propertyDetailFromRTK?.propertyid) {
            getUserRolePropertyList();
            fetchChatHistory(1, false);

            // Setup SignalR connection
            const connection = new HubConnectionBuilder()
                .withUrl(API_URL.BASE_API_URL.LogAndMessage, { withCredentials: false })
                .withAutomaticReconnect()
                .build();

            connection
                .start()
                .then(() => {
                    if (userDetail?.userid) {
                        connection.send("connectpropertychat", userDetail.userid + "#P:" + propertyDetailFromRTK.propertyid);
                    }
                })

            connection.on("receivepropertychat", (data) => {
                if (
                    data &&
                    parseInt(data.propertyid) === parseInt(propertyDetailFromRTK.propertyid)
                ) {
                    setMessages((prev) => {
                        if (prev.some((m) => m.chatid === data.chatid)) return prev;
                        return [...prev, data];
                    });

                    setTimeout(() => {
                        if (chatContainerRef.current) {
                            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                        }
                    }, 100);
                }
            });

            hubConnectionRef.current = connection;

            return () => {
                if (connection) {
                    connection.stop();
                }
            };
        } else {
            setMessages([]);
            setPageIndex(1);
            setHasMore(false);
        }
    }, [show, propertyDetailFromRTK?.propertyid]);

    // Handle scroll for loading older messages (infinite scroll up)
    const handleScroll = () => {
        const container = chatContainerRef.current;
        if (!container) return;

        if (container.scrollTop === 0 && hasMore && !loadingMore && !loading) {
            fetchChatHistory(pageIndex + 1, true);
        }
    };

    // Input change and @ mention detection
    const handleInputChange = (e) => {
        const value = e.target.value;
        const cursorPosition = e.target.selectionStart;
        setInputMessage(value);

        // Find if @ was typed
        const lastAt = value.lastIndexOf("@", cursorPosition - 1);
        if (lastAt !== -1 && (lastAt === 0 || /\s/.test(value[lastAt - 1]))) {
            const query = value.substring(lastAt + 1, cursorPosition);
            if (!/\s/.test(query)) {
                setAtIndex(lastAt);

                const filtered = userList.filter((u) =>
                    (u.displayname || u.email || "")
                        .toLowerCase()
                        .includes(query.toLowerCase())
                );
                setFilteredMentionUsers(filtered);
                setShowMentionPopup(filtered.length > 0);
                return;
            }
        }
        setShowMentionPopup(false);
    };

    // Select user from mention popup
    const handleSelectMention = (user) => {
        if (atIndex === -1) return;

        const beforeAt = inputMessage.substring(0, atIndex);
        const cursor = inputRef.current ? inputRef.current.selectionStart : inputMessage.length;
        const afterCursor = inputMessage.substring(cursor);

        const displayName = user.displayname || user.email;
        const insertedMention = `@${displayName} `;
        const newText = beforeAt + insertedMention + afterCursor;

        setInputMessage(newText);
        setShowMentionPopup(false);

        if (!taggedUsers.some((u) => u.userid === user.userid)) {
            setTaggedUsers((prev) => [...prev, user]);
        }

        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Send Message
    const handleSendMessage = async () => {
        if (!inputMessage.trim() || !propertyDetailFromRTK?.propertyid) return;

        const messageToSend = inputMessage.trim();
        const payload = {
            propertyid: parseInt(propertyDetailFromRTK.propertyid),
            propertycode: propertyDetailFromRTK.propertycode || "",
            senderuserid: parseInt(userDetail?.userid || 0),
            senderdisplayname: userDetail?.displayname || userDetail?.username || "User",
            senderemail: userDetail?.email || "",
            message: messageToSend,
            taggeduserids: taggedUsers.map((u) => u.userid).join(","),
        };

        setInputMessage("");
        setTaggedUsers([]);
        setShowMentionPopup(false);

        await apiCall(
            {
                method: "POST",
                url: API_URL.BASE_API_URL.Notification + API_URL.PROPERTY_CHAT.SAVE,
                body: payload,
            },
            false
        );

        if (payload) {
            if (payload.message && payload.propertycode) {
                if (payload.message.indexOf("@RM Copilot") >= 0) {
                    setRmCopilotThinking(true);
                    try {
                        let RMResponce = await apiCall(
                            {
                                method: "POST",
                                url: API_URL.PROPERTY_CHAT.RMCHATAPI,
                                body: {
                                    property_code: payload.propertycode,
                                    message: payload.message
                                },
                            },
                            false
                        );

                        if (RMResponce?.status === 1 && RMResponce?.data) {
                            const rmText = RMResponce.data.text || "";
                            const rmGraphs = RMResponce.data.graphs;

                            if (rmText || (Array.isArray(rmGraphs) && rmGraphs.length > 0)) {
                                await apiCall(
                                    {
                                        method: "POST",
                                        url:
                                            API_URL.BASE_API_URL.Notification +
                                            API_URL.PROPERTY_CHAT.SAVE,
                                        body: {
                                            propertyid: payload.propertyid,
                                            propertycode: payload.propertycode,
                                            senderuserid: RMAsDefaultUserData.userid,
                                            senderdisplayname: RMAsDefaultUserData.displayname,
                                            senderemail: RMAsDefaultUserData.email,
                                            message: rmText || " ",
                                            taggeduserids: String(userDetail?.userid || ""),
                                            graphdata:
                                                Array.isArray(rmGraphs) && rmGraphs.length > 0
                                                    ? JSON.stringify(rmGraphs)
                                                    : "",
                                        },
                                    },
                                    false
                                );
                            }
                        }
                    } catch (error) {
                    } finally {
                        setRmCopilotThinking(false);
                    }
                }
            }
        }
    };

    // Handle key press (Enter to send, Shift+Enter for newline)
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            if (showMentionPopup && filteredMentionUsers.length > 0) {
                e.preventDefault();
                handleSelectMention(filteredMentionUsers[0]);
            } else {
                e.preventDefault();
                handleSendMessage();
            }
        }
    };

    // Get profile picture dynamically from userList or userDetail based on senderuserid
    const getSenderProfilePic = (senderuserid) => {
        if (parseInt(senderuserid) === parseInt(userDetail?.userid) && userDetail?.profilepic) {
            return userDetail.profilepic;
        }
        const user = userList.find((u) => parseInt(u.userid) === parseInt(senderuserid));
        return user?.profilepic || userPlaceHolder;
    };

    // Format message text with highlighted mentions
    const renderFormattedMessage = (msgText) => {
        if (!msgText) return "";

        // Get user names that can be mentioned
        const userNames = userList
            .map((u) => u.displayname || u.email)
            .filter(Boolean);

        if (userNames.length === 0) {
            return msgText;
        }

        // Escape special regex characters
        const escapedNames = userNames.map((name) =>
            name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        );

        // Match only known users
        const mentionRegex = new RegExp(
            `@(${escapedNames.join("|")})(?=\\s|$)`,
            "g"
        );

        const parts = msgText.split(mentionRegex);

        return parts.map((part, index) => {
            const isUserName = userNames.some(
                (name) => name.toLowerCase() === part.toLowerCase()
            );

            if (isUserName) {
                return (
                    <span key={index} className="property-chat-mention">
                        @{part}
                    </span>
                );
            }

            return part;
        });
    };
    

    const parseGraphData = (graphdata) => {
        if (!graphdata) return [];
        try {
            const parsed = typeof graphdata === "string" ? JSON.parse(graphdata) : graphdata;
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch (error) {
            return [];
        }
    };

    const normalizeMarkdown = (text) => {
        if (!text) return "";

        return text
            .replace(/\\r\\n/g, "\n")
            .replace(/\\n/g, "\n")
            .replace(/\\r/g, "\n");
    };

    return (

        <div className="insightOffcanvasWrapper">
            <Offcanvas
                show={show}
                onHide={handleClose}
                placement="end"
                className="insightOffcanvasWrapper property-chat-offcanvas"
            >
                <Offcanvas.Header closeButton className="insightOffcanvasMain">
                    <Offcanvas.Title className="d-flex align-items-center gap-2">
                        <i className="fa fa-comments text-primary"></i>
                        <span>
                            {propertyDetailFromRTK
                                ? propertyDetailFromRTK.propertyname
                                : "First Select Property"}
                            {propertyDetailFromRTK
                                ? " (" + propertyDetailFromRTK.propertycode + ")"
                                : ""}
                        </span>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body id="canvaschat" className="p-0 d-flex flex-column h-100 position-relative">
                    <div
                        className="chat-messages-container"
                        ref={chatContainerRef}
                        onScroll={handleScroll}
                    >
                        {loadingMore && (
                            <div className="text-center py-2">
                                <Spinner animation="border" size="sm" variant="primary" />
                                <span className="ms-2 text-muted fs-12">Loading history...</span>
                            </div>
                        )}

                        {loading && !loadingMore ? (
                            <div className="d-flex justify-content-center align-items-center h-100">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="d-flex flex-column justify-content-center align-items-center h-100 text-muted">
                                <i className="fa fa-comments fs-36 mb-2 opacity-50"></i>
                                <p className="mb-0 fw-500">No chat messages yet.</p>
                                <small>Type a message or use @ to tag team members.</small>
                            </div>
                        ) : (
                            messages.map((msg, index) => {
                                const isMe = parseInt(msg.senderuserid) === parseInt(userDetail?.userid);
                                const isRmCopilot = parseInt(msg.senderuserid) === RMAsDefaultUserData.userid;
                                const graphs = isRmCopilot ? parseGraphData(msg.graphdata) : [];
                                return (
                                    <div
                                        key={msg.chatid || index}
                                        className={`chat-bubble-wrapper ${isMe ? "me" : "other"} ${isRmCopilot ? "rm-copilot" : ""}`}
                                    >
                                        <img
                                            src={getSenderProfilePic(msg.senderuserid)}
                                            alt={msg.senderdisplayname || "User"}
                                            className="chat-avatar"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = userPlaceHolder;
                                            }}
                                        />
                                        <div className="flex-grow-1 min-width-0">
                                            {!isMe && (
                                                <div className="chat-sender-name">
                                                    {msg.senderdisplayname || msg.senderemail || "User"}
                                                </div>
                                            )}
                                            <div className="chat-bubble">
                                                {isRmCopilot ? (
                                                    <div className="rm-copilot-markdown">
                                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                            {normalizeMarkdown(msg.message)}
                                                        </ReactMarkdown>
                                                    </div>
                                                ) : (
                                                    <div>{renderFormattedMessage(msg.message)}</div>
                                                )}

                                                {graphs.length > 0 && (
                                                    <div className="rm-copilot-graphs">
                                                        {graphs.map((graph, gIndex) => (
                                                            <div
                                                                className="rm-copilot-graph"
                                                                key={`${msg.chatid || index}-graph-${gIndex}`}
                                                            >
                                                                <RMCopilotChart graph={graph} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                <div className="chat-time">
                                                    {msg.createdon
                                                        ? moment(msg.createdon).format("hh:mm A")
                                                        : ""}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}

                        {rmCopilotThinking && (
                            <div className="text-muted d-flex align-items-center gap-2 fs-12 ps-1">
                                <Spinner animation="grow" size="sm" variant="primary" />
                                RM Copilot is analyzing...
                            </div>
                        )}
                    </div>

                    {/* Input Area */}
                    <div className="chat-input-area">
                        {/* Tagged users indicator */}
                        {taggedUsers.length > 0 && (
                            <div className="d-flex flex-wrap gap-1 mb-2">
                                {taggedUsers.map((user) => (
                                    <Badge
                                        key={user.userid}
                                        bg="info"
                                        className="d-flex align-items-center gap-1 font-normal"
                                    >
                                        @{user.displayname}
                                        <i
                                            className="fa fa-times cursor-pointer ms-1"
                                            onClick={() =>
                                                setTaggedUsers((prev) =>
                                                    prev.filter((u) => u.userid !== user.userid)
                                                )
                                            }
                                        ></i>
                                    </Badge>
                                ))}
                            </div>
                        )}

                        {/* @ Mention Popup */}
                        {showMentionPopup && (
                            <div className="mention-popup">
                                <div className="px-3 py-1 bg-light border-bottom text-muted fw-bold fs-11">
                                    Tag User
                                </div>
                                {filteredMentionUsers.map((user) => (
                                    <div
                                        key={user.userid}
                                        className="mention-item"
                                        onClick={() => handleSelectMention(user)}
                                    >
                                        <img
                                            src={user.profilepic || userPlaceHolder}
                                            alt={user.displayname}
                                            className="mention-item-avatar"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = userPlaceHolder;
                                            }}
                                        />
                                        <div>
                                            <div className="fw-600 fs-13">
                                                {user.displayname}
                                            </div>
                                            {user.email && (
                                                <div className="text-muted fs-11">
                                                    {user.email}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Textarea and Send button */}
                        <div className="chat-input-wrapper">
                            <textarea
                                ref={inputRef}
                                className="form-control chat-textarea"
                                rows={2}
                                placeholder="Type a message... (Use @ to mention someone)"
                                value={inputMessage}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                disabled={!propertyDetailFromRTK?.propertyid}
                            />
                            <button id="chatpage_button-onclick" class="send-btn" onClick={handleSendMessage} disabled={!inputMessage.trim() || !propertyDetailFromRTK?.propertyid}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3565FC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-send">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M10 14l11 -11"></path><path d="M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -7l-7 -3.5a.55 .55 0 0 1 0 -1l18 -6.5"></path>
                                </svg>
                            </button>
                            {/*<button*/}
                            {/*    className="send-btn"*/}
                            {/*    onClick={handleSendMessage}*/}
                            {/*    disabled={!inputMessage.trim() || !propertyDetailFromRTK?.propertyid}*/}
                            {/*>*/}
                            {/*    Send*/}
                            {/*</button>*/}
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
}

export default PropertyChat;
